import React, { useState, useEffect } from 'react'
import axios from 'axios'
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  LinearProgress,
  Fade,
  useTheme,
  CardActions,
  Divider,
  Alert
} from '@mui/material'
import {
  ArrowBack,
  ArrowForward,
  School,
  AdminPanelSettings,
  Edit,
  Visibility,
  Assignment,
  Security,
  Speed,
  Group,
  CheckCircle,
  Star,
  TrendingUp,
  Info,
  Gavel,
  Policy,
  VerifiedUser,
  Article,
  PictureAsPdf,
  MenuBook,
  Description
} from '@mui/icons-material'

const AcercaDe = () => {
  const theme = useTheme()
  const [currentSlide, setCurrentSlide] = useState(0)
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')

  const slides = [
    {
      id: 1,
      title: "SGC ENSAS",
      subtitle: "Sistema de Gestión de Calidad",
      content: (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <School sx={{ fontSize: 140, color: 'primary.main', mb: 3 }} />
          <Typography variant="h2" gutterBottom color="primary.main" fontWeight="bold">
            Sistema de Gestión de Calidad
          </Typography>
          <Typography variant="h3" gutterBottom color="text.secondary">
            Escuela Normal Superior del Alto Sinú
          </Typography>
          <Typography variant="h5" sx={{ mt: 3, maxWidth: 700, mx: 'auto', lineHeight: 1.6, fontWeight: 'medium' }}>
            Plataforma digital integral para la gestión, documentación y control de procedimientos
            institucionales bajo estándares de calidad educativa.
          </Typography>
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Chip label="Calidad Educativa" color="primary" variant="outlined" size="large" sx={{ fontSize: '1rem', py: 1 }} />
            <Chip label="Gestión Digital" color="secondary" variant="outlined" size="large" sx={{ fontSize: '1rem', py: 1 }} />
            <Chip label="Mejora Continua" color="success" variant="outlined" size="large" sx={{ fontSize: '1rem', py: 1 }} />
          </Box>
        </Box>
      )
    },
    {
      id: 2,
      title: "Importancia del SGC",
      subtitle: "¿Por qué es crucial para ENSAS?",
      content: (
        <Grid container spacing={3} sx={{ py: 2 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', background: 'linear-gradient(45deg, #e3f2fd, #f3e5f5)' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                <Typography variant="h4" gutterBottom fontWeight="bold">
                  Acreditación y Cumplimiento
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  Garantiza el cumplimiento de estándares del MEN y facilita procesos de acreditación
                  institucional y de programas académicos.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', background: 'linear-gradient(45deg, #fff3e0, #e8f5e8)' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <TrendingUp sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
                <Typography variant="h4" gutterBottom fontWeight="bold">
                  Mejora Continua
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  Facilita la identificación de oportunidades de mejora y la implementación
                  de acciones correctivas y preventivas.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ background: 'linear-gradient(45deg, #ffebee, #f1f8e9)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Star sx={{ fontSize: 50, color: 'warning.main', mr: 2 }} />
                  <Typography variant="h4" fontWeight="bold">
                    Ventajas Institucionales
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="h6" fontWeight="bold" color="primary.main">
                      • Trazabilidad completa
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Seguimiento detallado de todos los procedimientos
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="h6" fontWeight="bold" color="primary.main">
                      • Eficiencia operativa
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Optimización de tiempos y recursos institucionales
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="h6" fontWeight="bold" color="primary.main">
                      • Transparencia
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Acceso controlado y auditable a la información
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )
    },
    {
      id: 3,
      title: "Marco Normativo",
      subtitle: "Normas técnicas que rigen el SGC ENSAS",
      content: (
        <Box sx={{ py: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ p: 3, background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', color: 'white', textAlign: 'center', mb: 3 }}>
                <VerifiedUser sx={{ fontSize: 100, mb: 2 }} />
                <Typography variant="h3" fontWeight="bold" gutterBottom>
                  Fundamento Legal y Técnico
                </Typography>
                <Typography variant="h5">
                  El SGC ENSAS se basa en estándares nacionales e internacionales de calidad
                </Typography>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', border: '3px solid', borderColor: 'primary.main', p: 3 }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Article sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h4" fontWeight="bold" color="primary.main" gutterBottom>
                    NTC 5555 - ICONTEC
                  </Typography>
                  <Typography variant="h6" color="text.secondary" fontWeight="bold">
                    Norma Técnica Colombiana
                  </Typography>
                </Box>
                <Typography variant="h6" paragraph fontWeight="bold">
                  Sistemas de gestión de la calidad para instituciones de educación superior.
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Establece los requisitos específicos para implementar y mantener un sistema de gestión
                  de calidad en instituciones de educación superior, asegurando la mejora continua en:
                </Typography>
                <Box component="ul" sx={{ textAlign: 'left', pl: 2 }}>
                  <Typography component="li" variant="body1">Procesos académicos y administrativos</Typography>
                  <Typography component="li" variant="body1">Gestión de recursos educativos</Typography>
                  <Typography component="li" variant="body1">Satisfacción de estudiantes y stakeholders</Typography>
                  <Typography component="li" variant="body1">Evaluación y mejora continua</Typography>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', border: '3px solid', borderColor: 'secondary.main', p: 3 }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Gavel sx={{ fontSize: 80, color: 'secondary.main', mb: 2 }} />
                  <Typography variant="h4" fontWeight="bold" color="secondary.main" gutterBottom>
                    NTCGP 1000:2009
                  </Typography>
                  <Typography variant="h6" color="text.secondary" fontWeight="bold">
                    Gestión Pública de Calidad
                  </Typography>
                </Box>
                <Typography variant="h6" paragraph fontWeight="bold">
                  Sistema de gestión de la calidad para entidades públicas colombianas.
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                  Versión actualizada de 2009, basada en ISO 9000 pero adaptada
                  a la administración pública colombiana. ENSAS la implementa para:
                </Typography>
                <Box component="ul" sx={{ textAlign: 'left', pl: 2 }}>
                  <Typography component="li" variant="body1">Mejorar calidad de servicios educativos</Typography>
                  <Typography component="li" variant="body1">Aumentar satisfacción ciudadana</Typography>
                  <Typography component="li" variant="body1">Optimizar desempeño institucional</Typography>
                  <Typography component="li" variant="body1">Promover mejora continua</Typography>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={{ p: 3, background: 'linear-gradient(45deg, #e8f5e8, #f0f8ff)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Policy sx={{ fontSize: 60, color: 'success.main', mr: 2 }} />
                  <Typography variant="h4" fontWeight="bold">
                    Marco Legal de Escuelas Normales
                  </Typography>
                </Box>
                <Box sx={{ mb: 3, p: 3, border: '2px solid', borderColor: 'primary.main', borderRadius: 2, bgcolor: 'primary.50' }}>
                  <Typography variant="h5" fontWeight="bold" color="primary.main" gutterBottom>
                    🏛️ Ley 2481 de 2025 - Ley Orgánica sobre Escuelas Normales
                  </Typography>
                  <Typography variant="h6" color="text.secondary" paragraph>
                    <strong>Nuevo marco normativo</strong> que establece el fundamento legal para las Escuelas Normales Superiores,
                    las autoriza a ofrecer educación superior y fortalece su papel en la formación docente para preescolar, básica y media.
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body1" fontWeight="bold" color="primary.main">• Participación social</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body1" fontWeight="bold" color="primary.main">• Movilidad académica</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body1" fontWeight="bold" color="primary.main">• Redes colaborativas</Typography>
                    </Grid>
                  </Grid>
                </Box>
                <Typography variant="h6" fontWeight="bold" color="success.main" gutterBottom sx={{ mb: 2 }}>
                  Complemento Normativo
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body1" fontWeight="bold" color="success.main">
                      • Decreto 1330 de 2019
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Registro calificado de programas
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body1" fontWeight="bold" color="success.main">
                      • Resolución 021795 de 2020
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Condiciones de calidad MEN
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body1" fontWeight="bold" color="success.main">
                      • ISO 9001:2015
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Estándar internacional de calidad
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body1" fontWeight="bold" color="success.main">
                      • CNA - CESU
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Lineamientos de acreditación
                    </Typography>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )
    },
    {
      id: 4,
      title: "Roles y Responsabilidades",
      subtitle: "Perfiles de usuario en el sistema",
      content: (
        <Grid container spacing={3} sx={{ py: 2 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', border: '2px solid', borderColor: 'error.main' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 2, bgcolor: 'error.main' }}>
                  <AdminPanelSettings sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h4" gutterBottom fontWeight="bold" color="error.main">
                  Administrador
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                  Control total del sistema
                </Typography>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="h6" gutterBottom>
                    <strong>Responsabilidades:</strong>
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    • Gestión completa de usuarios<br/>
                    • Configuración del sistema<br/>
                    • Supervisión de auditorías<br/>
                    • Acceso a todas las funcionalidades<br/>
                    • Gestión de roles y permisos
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', border: '2px solid', borderColor: 'warning.main' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 2, bgcolor: 'warning.main' }}>
                  <Edit sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h4" gutterBottom fontWeight="bold" color="warning.main">
                  Editor
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                  Creación y modificación de contenido
                </Typography>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="h6" gutterBottom>
                    <strong>Responsabilidades:</strong>
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    • Crear nuevos procedimientos<br/>
                    • Editar procedimientos de su gestión<br/>
                    • Gestionar actividades y diagramas<br/>
                    • Generar documentación<br/>
                    • Participar en auditorías
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: '100%', border: '2px solid', borderColor: 'info.main' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 2, bgcolor: 'info.main' }}>
                  <Visibility sx={{ fontSize: 40 }} />
                </Avatar>
                <Typography variant="h4" gutterBottom fontWeight="bold" color="info.main">
                  Consultor
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                  Acceso de solo lectura
                </Typography>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="h6" gutterBottom>
                    <strong>Responsabilidades:</strong>
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    • Consultar procedimientos<br/>
                    • Visualizar diagramas<br/>
                    • Exportar documentación<br/>
                    • Búsqueda avanzada<br/>
                    • Seguimiento de procesos
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )
    },
    {
      id: 5,
      title: "Funcionalidades Principales",
      subtitle: "Herramientas disponibles en el sistema",
      content: (
        <Grid container spacing={3} sx={{ py: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ textAlign: 'center', p: 3, height: '100%' }}>
              <Assignment sx={{ fontSize: 70, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Gestión de Procedimientos
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Creación, edición y control de versiones de procedimientos institucionales
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ textAlign: 'center', p: 3, height: '100%' }}>
              <Speed sx={{ fontSize: 70, color: 'secondary.main', mb: 2 }} />
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Diagramas con IA
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Generación automática de diagramas de flujo usando inteligencia artificial
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ textAlign: 'center', p: 3, height: '100%' }}>
              <Security sx={{ fontSize: 70, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Auditoría y Control
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Trazabilidad completa de cambios y sistema de auditoría integrado
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ textAlign: 'center', p: 3, height: '100%' }}>
              <Group sx={{ fontSize: 70, color: 'warning.main', mb: 2 }} />
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Colaboración
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Trabajo colaborativo con roles definidos y control de permisos
              </Typography>
            </Card>
          </Grid>
        </Grid>
      )
    },
    {
      id: 6,
      title: "Tecnología y Beneficios",
      subtitle: "Innovación al servicio de la calidad educativa",
      content: (
        <Box sx={{ py: 4 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  🤖 Inteligencia Artificial
                </Typography>
                <Typography variant="h6" paragraph>
                  • Generación automática de descripciones<br/>
                  • Diagramas de flujo inteligentes<br/>
                  • Asistencia en documentación<br/>
                  • Optimización de procesos
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ p: 4, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  ☁️ Plataforma Web
                </Typography>
                <Typography variant="h6" paragraph>
                  • Acceso desde cualquier dispositivo<br/>
                  • Actualizaciones en tiempo real<br/>
                  • Backup automático<br/>
                  • Interfaz intuitiva y moderna
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h3" gutterBottom color="primary.main" fontWeight="bold">
                  Comprometidos con la Excelencia Educativa
                </Typography>
                <Typography variant="h5" color="text.secondary" sx={{ maxWidth: 900, mx: 'auto' }}>
                  El SGC ENSAS representa nuestro compromiso con la calidad, la transparencia y la mejora
                  continua en todos los procesos institucionales, contribuyendo a la formación de
                  educadores de excelencia para el Alto Sinú y Colombia.
                </Typography>
                <Box sx={{ mt: 3 }}>
                  <Chip label="Versión 1.0" color="primary" size="large" sx={{ mr: 1, fontSize: '1rem' }} />
                  <Chip label="2025" color="secondary" size="large" sx={{ mr: 1, fontSize: '1rem' }} />
                  <Chip label="ENSAS" color="success" size="large" sx={{ fontSize: '1rem' }} />
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )
    }
  ]

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length)
  }

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length)
  }

  const goToSlide = (index) => {
    setCurrentSlide(index)
  }

  // Navegación con teclado
  useEffect(() => {
    const handleKeyPress = (event) => {
      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault()
          prevSlide()
          break
        case 'ArrowRight':
        case 'ArrowDown':
        case ' ': // Barra espaciadora
          event.preventDefault()
          nextSlide()
          break
        case 'Home':
          event.preventDefault()
          goToSlide(0)
          break
        case 'End':
          event.preventDefault()
          goToSlide(slides.length - 1)
          break
        case 'Escape':
          event.preventDefault()
          // Podrías agregar aquí funcionalidad para salir del modo presentación
          break
        default:
          // Navegación por números (1-6)
          const slideNumber = parseInt(event.key)
          if (slideNumber >= 1 && slideNumber <= slides.length) {
            event.preventDefault()
            goToSlide(slideNumber - 1)
          }
          break
      }
    }

    // Agregar event listener
    document.addEventListener('keydown', handleKeyPress)

    // Cleanup: remover event listener cuando el componente se desmonte
    return () => {
      document.removeEventListener('keydown', handleKeyPress)
    }
  }, [slides.length]) // Dependencia para actualizar si cambia el número de slides

  const currentSlideData = slides[currentSlide]

  // Función para descargar el manual de usuario en PDF
  const handleDownloadManual = async () => {
    setLoading(true)
    setError('')
    setSuccess('')

    try {
      const token = localStorage.getItem('sgc_token')
      const response = await axios.get('/api/export/manual-usuario/pdf', {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      })

      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const fecha = new Date().toISOString().slice(0, 10).replace(/-/g, '')
      link.download = `Manual_Usuario_SGC_ENSAS_${fecha}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      setSuccess('Manual descargado exitosamente')
      setTimeout(() => setSuccess(''), 3000)
    } catch (error) {
      console.error('Error al descargar manual:', error)
      setError('Error al descargar el manual: ' + (error.response?.data?.detail || error.message))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
          <Info sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h3" component="h1" fontWeight="bold" color="primary.main">
            Acerca del SGC ENSAS
          </Typography>
        </Box>
        <Typography variant="h6" color="text.secondary">
          Sistema de Gestión de Calidad - Escuela Normal Superior del Alto Sinú
        </Typography>
      </Box>

      {/* Alertas de éxito/error */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Dos Cards: Acerca de y Manual de Usuario */}
      <Grid container spacing={4}>
        {/* Card 1: Acerca de */}
        <Grid item xs={12} md={6}>
          <Card
            elevation={8}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: 12
              }
            }}
          >
            <CardContent sx={{ flexGrow: 1, p: 4 }}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <School sx={{ fontSize: 80, mb: 2 }} />
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  Acerca del Sistema
                </Typography>
              </Box>

              <Divider sx={{ my: 2, bgcolor: 'rgba(255,255,255,0.3)' }} />

              <Typography variant="h6" paragraph>
                <strong>Sistema de Gestión de Calidad</strong>
              </Typography>
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
                Plataforma digital integral para la gestión, documentación y control de procedimientos
                institucionales bajo estándares de calidad educativa.
              </Typography>

              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                Características Principales:
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <Typography component="li" variant="body1" paragraph>
                  Gestión completa de procedimientos institucionales
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Generación automática de diagramas con IA
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Sistema de auditoría y trazabilidad
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Exportación a PDF y múltiples formatos
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  Control de acceso por roles y permisos
                </Typography>
              </Box>

              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                Marco Normativo:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip label="NTC 5555 - ICONTEC" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                <Chip label="NTCGP 1000:2009" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                <Chip label="Ley 2481 de 2025" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                <Chip label="ISO 9001:2015" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
              </Box>

              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Chip label="Versión 1.0" size="large" sx={{ mr: 1, bgcolor: 'rgba(255,255,255,0.3)', color: 'white' }} />
                <Chip label="2025" size="large" sx={{ mr: 1, bgcolor: 'rgba(255,255,255,0.3)', color: 'white' }} />
                <Chip label="ENSAS" size="large" sx={{ bgcolor: 'rgba(255,255,255,0.3)', color: 'white' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Card 2: Manual de Usuario */}
        <Grid item xs={12} md={6}>
          <Card
            elevation={8}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: 12
              }
            }}
          >
            <CardContent sx={{ flexGrow: 1, p: 4 }}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <MenuBook sx={{ fontSize: 80, mb: 2 }} />
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                  Manual de Usuario
                </Typography>
              </Box>

              <Divider sx={{ my: 2, bgcolor: 'rgba(255,255,255,0.3)' }} />

              <Typography variant="h6" paragraph>
                <strong>Guía Completa del Sistema</strong>
              </Typography>
              <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
                Accede al manual completo de usuario con instrucciones detalladas sobre todas las
                funcionalidades del sistema SGC ENSAS.
              </Typography>

              <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
                Contenido del Manual:
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Capítulo 1:</strong> Introducción al Sistema
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Capítulo 2:</strong> Roles y Responsabilidades
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Capítulo 3:</strong> Primeros Pasos
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Capítulo 4:</strong> Gestión de Procedimientos
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Capítulo 5:</strong> Generación de Diagramas con IA
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Capítulo 6:</strong> Búsqueda y Exportación
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Capítulo 7:</strong> Gestiones y Procesos
                </Typography>
                <Typography component="li" variant="body1" paragraph>
                  <strong>Capítulo 8:</strong> Perfil y Configuración
                </Typography>
              </Box>

              <Box sx={{
                mt: 3,
                p: 2,
                bgcolor: 'rgba(255,255,255,0.2)',
                borderRadius: 2,
                textAlign: 'center'
              }}>
                <Description sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  Formato PDF - Descarga Gratuita
                </Typography>
                <Typography variant="caption">
                  Compatible con cualquier lector de PDF
                </Typography>
              </Box>
            </CardContent>

            <CardActions sx={{ p: 3, pt: 0, justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<PictureAsPdf />}
                onClick={handleDownloadManual}
                disabled={loading}
                sx={{
                  bgcolor: 'white',
                  color: '#f5576c',
                  fontWeight: 'bold',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.9)',
                    transform: 'scale(1.05)'
                  },
                  '&:disabled': {
                    bgcolor: 'rgba(255,255,255,0.5)',
                    color: 'rgba(245,87,108,0.5)'
                  }
                }}
              >
                {loading ? 'Generando PDF...' : 'Descargar Manual'}
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      {/* Changelog */}
      <Box sx={{ mt: 6 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Info color="primary" />
          Historial de Versiones
        </Typography>

        <Card sx={{ mt: 3, boxShadow: 3 }}>
          <CardContent>
            {/* Versión 2.0 */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" fontWeight="bold" color="primary.main">
                  Versión 2.0
                </Typography>
                <Chip label="Actual" color="success" />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Octubre 2025
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }}>
                🎨 Nuevas Funcionalidades
              </Typography>

              <Typography component="div" variant="body2" paragraph>
                <strong>• Sistema de Colores por Gestión:</strong>
                <br />
                - Identificación visual con colores únicos para cada gestión
                <br />
                - Aplicado en Dashboard, listas, vistas detalladas y formularios
                <br />
                - 10 colores suaves y complementarios
                <br />
                - Chips de código con fondo sólido, prefijos con transparencia
              </Typography>

              <Typography component="div" variant="body2" paragraph>
                <strong>• Papelera de Procedimientos:</strong>
                <br />
                - Recuperación de procedimientos eliminados
                <br />
                - Guardado completo: código, actividades, responsables, diagramas
                <br />
                - Vista exclusiva para administradores
                <br />
                - Registro de quién eliminó, cuándo y por qué
              </Typography>

              <Typography component="div" variant="body2" paragraph>
                <strong>• Gestión Avanzada de Sesiones:</strong>
                <br />
                - Timeout automático de 1 hora por inactividad
                <br />
                - Vista de usuarios en línea en tiempo real
                <br />
                - Detección de IP real (detrás de proxies)
                <br />
                - Cerrar sesiones individuales o todas las sesiones de un usuario
              </Typography>

              <Typography component="div" variant="body2" paragraph>
                <strong>• Actividad Individual por Usuario:</strong>
                <br />
                - Historial completo de acciones de cada usuario
                <br />
                - Registro de logins, logouts, creaciones, ediciones
                <br />
                - IP y navegador de cada acción
                <br />
                - Filtros personalizables (25, 50, 100, 200 registros)
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                🔧 Mejoras
              </Typography>

              <Typography component="div" variant="body2" paragraph>
                • Exportación PNG mejorada en Constructor de Diagramas (alta resolución, fondo blanco)
                <br />
                • Detección correcta de IP real del cliente detrás de proxies
                <br />
                • Sistema de permisos mejorado: gestiones activas (puede editar) vs. solo lectura
                <br />
                • Colores aplicados consistentemente en todas las vistas del sistema
                <br />
                • Auditoría mejorada con captura de IP en todas las acciones
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                🐛 Correcciones
              </Typography>

              <Typography component="div" variant="body2">
                • Corregida autenticación en módulos de normatividad y procesos
                <br />
                • Solucionado problema de exportación PNG en diagramas
                <br />
                • Corregida visualización de colores en Dashboard y vistas detalladas
                <br />
                • Mejorado manejo de sesiones expiradas con mensaje al usuario
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Versión 1.0 */}
            <Box>
              <Typography variant="h5" fontWeight="bold" color="text.secondary">
                Versión 1.0
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Agosto 2024
              </Typography>

              <Typography variant="body2" paragraph>
                <strong>Lanzamiento inicial del Sistema de Gestión de Calidad</strong>
              </Typography>

              <Typography component="div" variant="body2" paragraph>
                • Gestión completa de procedimientos documentados
                <br />
                • Sistema de roles y permisos (Administrador, Rector, Editor, Consultor)
                <br />
                • Editor de diagramas de flujo con Mermaid
                <br />
                • Control de versiones de procedimientos
                <br />
                • Exportación a PDF
                <br />
                • Búsqueda global avanzada
                <br />
                • Sistema de auditoría completo
                <br />
                • Gestión de usuarios y configuración
                <br />
                • Notificaciones en tiempo real
                <br />
                • Biblioteca de definiciones y normatividad
                <br />
                • Generación de contenido con IA
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Footer */}
      <Box sx={{ textAlign: 'center', mt: 6, p: 3, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 2 }}>
        <Typography variant="h5" gutterBottom color="primary.main" fontWeight="bold">
          Somos una oportunidad posibilitante
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto', lineHeight: 1.8 }}>
          El SGC ENSAS representa nuestro compromiso con la calidad, la transparencia y la mejora
          continua en todos los procesos institucionales, contribuyendo a la formación de
          educadores de excelencia para el Alto Sinú y Colombia.
        </Typography>
      </Box>
    </Box>
  )
}

export default AcercaDe